import React from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import { RouteWhenAuthenticated } from 'common/components';
import NotFound from 'core/NotFound';
import BrandRoutes from 'core/routes/BrandRoutes';
import EnterpriseRoutes from 'core/routes/EnterpriseRoutes';
import { CreateAdCampaignDeeplink } from 'modules/campaigns/components/ad-campaign/create-campaign-deeplink/CreateAdCampaignDeeplink';
import IdentityVerificationRoutes from 'modules/compliance/kyc-process/IdentityVerificationRoutes';
import DeveloperPreviews from 'modules/developer-previews';
import { Experience } from 'modules/experience';
import { FrontPage } from 'modules/front-page';
import { InviteToken } from 'modules/invites';
import { Login, EmailLogin } from 'modules/login';
import OnboardingPage from 'modules/onboarding-page/OnboardingPage';
import PhotoShootRoutes from 'modules/photo-shoot/PhotoShootRoutes/PhotoShootRoutes';
import SelfServeSignupWizard from 'modules/self-serve-sign-up/SelfServeSignupFlow/SelfServeSignupWizard';
import WelcomeView from 'modules/self-serve-sign-up/SelfServeSignupFlow/WelcomeView';
import VenueDataCollection from 'modules/venue-data-collection';

import MerchantRoutes from './MerchantRoutes';
import VenueRoutes from './VenueRoutes';
import MerchantSignUp from '../../modules/merchant-sign-up';
import ExistingMerchantSignUp from '../../modules/merchant-sign-up/ExistingMerchantSignUp';
import InvitedUserSignUp from '../../modules/merchant-sign-up/InvitedUserSignUp';
import MerchantSelfServeSignUp from '../../modules/merchant-sign-up/MerchantSelfServeSignUp';
import SignUpRedirectWrapper from '../../modules/merchant-sign-up/SignUpRedirectWrapper';
import { TaxReporting } from '../../modules/payout-reports/TaxReporting';
import { RouteWhenNotAuthenticated } from '../../modules/self-serve-sign-up';
import SelfServeSignUp from '../../modules/self-serve-sign-up';
import SignContract from '../../modules/self-serve-sign-up/SignContract/SignContract';

type Props = {
  forceIDVerification: boolean;
};

const isDevEnv = process.env.APP_ENV === 'development';

const RootRoutes = ({ forceIDVerification }: Props) => {
  // Remove trailing slash from URL
  const { pathname, search } = useLocation();
  // .slice(1) is important, because the root route always has a trailing slash
  if (pathname.slice(1).endsWith('/')) {
    return <Redirect to={`${pathname.slice(0, -1)}${search}`} />;
  }

  return (
    <Switch>
      <Route exact path="/login" component={Login} />
      <Route exact path="/email_login" component={EmailLogin} />
      <Route path="/photo-shoot/:venueId" component={PhotoShootRoutes} />
      <Route path="/merchant-sign-up" component={SignUpRedirectWrapper} />
      <Route path="/existing-merchant-sign-up" component={ExistingMerchantSignUp} />
      <Route path="/draft-merchant-sign-up" component={MerchantSignUp} />
      <Route path="/invited-user-sign-up" component={InvitedUserSignUp} />
      <Route path="/merchant-self-serve-signup" component={MerchantSelfServeSignUp} />
      <Route path="/experience" component={Experience} />
      <Route path="/tax-reporting" component={TaxReporting} />

      <RouteWhenAuthenticated path="/venue-data-collection/:merchantId" component={VenueDataCollection} />
      {isDevEnv && <RouteWhenAuthenticated path="/:feature(onboarding-page)" component={OnboardingPage} />}

      <RouteWhenAuthenticated path="/identity-verification" component={IdentityVerificationRoutes} />
      {forceIDVerification && <Redirect to="/identity-verification" />}

      <RouteWhenAuthenticated path="/:feature(enterprise)/:slug/brand/:brandId" component={BrandRoutes} />
      <RouteWhenAuthenticated path="/:feature(enterprise)/:slug" component={EnterpriseRoutes} />
      <RouteWhenAuthenticated path="/:feature(merchant)/:slug" component={MerchantRoutes} />

      <RouteWhenAuthenticated exact path="/" component={FrontPage} />

      <RouteWhenAuthenticated path="/:feature(venue)/:slug" component={VenueRoutes} />
      <RouteWhenAuthenticated exact path="/invite/:token" component={InviteToken} />

      <RouteWhenNotAuthenticated path="/partner-with-wolt" component={SelfServeSignUp} />
      <RouteWhenAuthenticated exact path="/ad-campaign-creation" component={CreateAdCampaignDeeplink} />
      {isDevEnv && <RouteWhenAuthenticated path="/onboarding-welcome" component={WelcomeView} />}
      {isDevEnv && <RouteWhenAuthenticated path="/onboarding-wizard" component={SelfServeSignupWizard} />}
      {isDevEnv && <RouteWhenAuthenticated path="/onboarding-contract" component={SignContract} />}
      {process.env.APP_ENV !== 'production' && (
        <RouteWhenAuthenticated exact path="/developer-previews" component={DeveloperPreviews} />
      )}
      <Route component={NotFound} />
    </Switch>
  );
};

export default RootRoutes;
